import React, { useState, createContext, useRef, useCallback } from "react";
import rainSound from "../assets/dragon-studio-relaxing-rain-444802.mp3";

export const AmbientSoundContext = createContext();

const VOLUME_STORAGE_KEY = "zvukyviry_ambient_volume";

const AMBIENT_SOUNDS = {
  rain: rainSound,
};

export const TONE_VIDEO_ID = "mf-R3W_q170";
export const TONE_INFO = { artist: "Bartimeus", title: "Inspired Worship" };

const loadVolume = () => {
  try {
    const stored = localStorage.getItem(VOLUME_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 50;
  } catch {
    return 50;
  }
};

export const AmbientSoundProvider = ({ children }) => {
  const [activeSound, setActiveSound] = useState(null);
  const [volume, setVolume] = useState(loadVolume);
  const audioRef = useRef(null);
  const tonePlayerRef = useRef(null);
  const mainPlayerRef = useRef(null);
  const wasPlayingBeforeTone = useRef(false);

  const stopSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (tonePlayerRef.current) {
      try {
        tonePlayerRef.current.stopVideo();
      } catch (e) {
        console.warn("Could not stop tone player:", e);
      }
    }
    setActiveSound((prev) => {
      // Resume main player if we were playing tone
      if (prev === "tone" && wasPlayingBeforeTone.current && mainPlayerRef.current) {
        try {
          mainPlayerRef.current.playVideo();
        } catch (e) {
          // ignore
        }
      }
      return null;
    });
  }, []);

  const playSound = useCallback(
    (type) => {
      if (activeSound === type) {
        stopSound();
        return;
      }

      // Stop any currently playing sound
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
      if (tonePlayerRef.current) {
        try {
          tonePlayerRef.current.stopVideo();
        } catch (e) {
          // ignore
        }
      }

      // If previous was tone, resume main player before switching
      if (activeSound === "tone" && wasPlayingBeforeTone.current && mainPlayerRef.current) {
        try {
          mainPlayerRef.current.playVideo();
        } catch (e) {
          // ignore
        }
      }

      if (type === "tone") {
        // Pause main music player
        if (mainPlayerRef.current) {
          try {
            const state = mainPlayerRef.current.getPlayerState();
            wasPlayingBeforeTone.current = state === 1; // 1 = PLAYING
            mainPlayerRef.current.pauseVideo();
          } catch (e) {
            // ignore
          }
        }
        // Play via hidden YouTube player
        if (tonePlayerRef.current) {
          tonePlayerRef.current.loadVideoById(TONE_VIDEO_ID);
          tonePlayerRef.current.setVolume(volume);
        }
      } else {
        // Play local audio file
        const audio = new Audio(AMBIENT_SOUNDS[type]);
        audio.loop = true;
        audio.volume = volume / 100;
        audio.play().catch((err) => console.warn("Ambient audio play failed:", err));
        audioRef.current = audio;
      }

      setActiveSound(type);
    },
    [activeSound, volume, stopSound]
  );

  const setAmbientVolume = useCallback((vol) => {
    const clamped = Math.max(0, Math.min(100, vol));
    setVolume(clamped);
    localStorage.setItem(VOLUME_STORAGE_KEY, clamped.toString());
    if (audioRef.current) {
      audioRef.current.volume = clamped / 100;
    }
    if (tonePlayerRef.current) {
      try {
        tonePlayerRef.current.setVolume(clamped);
      } catch (e) {
        // ignore
      }
    }
  }, []);

  const setMainPlayer = useCallback((player) => {
    mainPlayerRef.current = player;
  }, []);

  return (
    <AmbientSoundContext.Provider
      value={{
        activeSound,
        volume,
        playSound,
        stopSound,
        setAmbientVolume,
        tonePlayerRef,
        setMainPlayer,
      }}
    >
      {children}
    </AmbientSoundContext.Provider>
  );
};

export default AmbientSoundContext;
